import { Card, Col, Row, Space, Tag, Typography } from 'antd'
import { Link } from 'react-router-dom'

import { Mascot, type Pose } from './mascot'

/**
 * The sections that carry the argument.
 *
 * Ordered by the hierarchy the brief asked for and most developer tools get
 * backwards: the business result first, the engineering result second, the
 * investigation third, and the technology last. A Go engineer who wants to know
 * about pprof will find it; one who wants to know why their bill went up should
 * not have to read about flame graphs to get there.
 *
 * Every number and capability on this page is one the product actually has.
 * Where something is not built, it is not here — there is no "coming soon"
 * section, because a homepage is read by people deciding whether to trust us.
 */

const INK = '#0f1419'
const MUTED = 'rgba(0,0,0,0.62)'

/**
 * Section shell, so the rhythm of the page is one decision rather than eight.
 *
 * The gopher, where a section takes one, sits in the top corner and is taken
 * out of the flow. In the flow it does two things wrong: inside a column of a
 * two-column row it pushes that column down and the headings stop lining up,
 * and beside the heading it makes the heading row as tall as the illustration,
 * which opens a gap between the title and the content that reads as a mistake.
 * Absolute puts it in the section's own padding, where there is already room.
 */
export function Section({
  title,
  lead,
  dark,
  mascot,
  children,
}: {
  title: string
  lead?: string
  dark?: boolean
  mascot?: Pose
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        background: dark ? INK : '#fff',
        color: dark ? '#fff' : INK,
        padding: '72px 24px',
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative' }}>
        {mascot && (
          <div style={{ position: 'absolute', top: -44, right: 0, lineHeight: 0, zIndex: 1 }}>
            {/* Poses drawn facing right are mirrored here: in the right-hand
                corner, facing right means facing off the edge of the page. */}
            <Mascot pose={mascot} size={104} flip={mascot === 'detective' || mascot === 'running'} />
          </div>
        )}
        <Typography.Title
          level={2}
          style={{
            color: dark ? '#fff' : INK,
            marginTop: 0,
            marginBottom: lead ? 12 : 32,
            maxWidth: mascot ? 860 : undefined,
          }}
        >
          {title}
        </Typography.Title>
        {lead && (
          <Typography.Paragraph
            style={{
              color: dark ? 'rgba(255,255,255,0.7)' : MUTED,
              fontSize: 17,
              maxWidth: 720,
              marginBottom: 32,
            }}
          >
            {lead}
          </Typography.Paragraph>
        )}
        {children}
      </div>
    </div>
  )
}

/**
 * What a regression actually looks like in the product.
 *
 * Built from the shape the detector really produces — a function, its share
 * either side of a deploy, and the fact that it also cost more in absolute
 * terms — rather than from the mock's cross-metric dashboard, which we do not
 * compute. The second condition is the interesting one and is why the alert is
 * trustworthy: a share can rise because something else got faster.
 */
export function RegressionExample() {
  return (
    <Card
      styles={{ body: { padding: 0 } }}
      style={{ background: '#12181f', borderColor: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}
    >
      <div
        style={{
          padding: '16px 0px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <Space wrap size={10} style={{ margin: 15 }}>
          <Tag color="red" style={{ margin: 0 }}>
            Regression
          </Tag>
          <Typography.Text style={{ color: '#fff', fontWeight: 600 }}>checkout-api</Typography.Text>
          <Typography.Text style={{ color: 'rgba(255,255,255,0.5)' }}>production</Typography.Text>
          <Typography.Text code style={{ color: '#69c0ff', background: 'transparent' }}>
            v1.4.2 → v1.4.3
          </Typography.Text>
        </Space>
        <Mascot pose="pointer" size={68} />
      </div>

      <div style={{ padding: '20px', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 460, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, textAlign: 'left' }}>
              <th style={{ paddingBottom: 8, fontWeight: 500 }}>CPU profile</th>
              <th style={{ paddingBottom: 8, fontWeight: 500 }}>before</th>
              <th style={{ paddingBottom: 8, fontWeight: 500 }}>after</th>
              <th style={{ paddingBottom: 8, fontWeight: 500 }}>change</th>
            </tr>
          </thead>
          <tbody style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}>
            {[
              ['encoding/json.Marshal', '8.4%', '19.1%', '+10.7'],
              ['checkout.buildPayload', '5.2%', '11.6%', '+6.4'],
              ['runtime.mallocgc', '11.8%', '16.0%', '+4.2'],
            ].map(([fn, before, after, delta]) => (
              <tr key={fn}>
                <td style={{ color: '#fff', padding: '6px 0' }}>{fn}</td>
                <td style={{ color: 'rgba(255,255,255,0.5)' }}>{before}</td>
                <td style={{ color: '#fff' }}>{after}</td>
                <td style={{ color: '#ff7875' }}>{delta} pts</td>
              </tr>
            ))}
          </tbody>
        </table>

        <Typography.Paragraph
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginTop: 16, marginBottom: 0 }}
        >
          Reported because the share grew <em>and</em> the absolute cost grew with it. A function
          whose share rises only because something else got faster is not a regression, and
          alerting on one is how a channel gets muted.
        </Typography.Paragraph>
      </div>
    </Card>
  )
}

/** The workflow this replaces, stated without drama. */
export function TheProblem() {
  const steps = [
    'Latency or cost rises. Metrics say something changed, not what.',
    'Somebody collects a pprof profile from a pod that is still running.',
    'They find an older profile to compare it against, if one was kept.',
    'They read two flame graphs side by side and try to spot the difference.',
    'They work backwards to which deploy it was.',
  ]

  return (
    <Row gutter={[32, 32]}>
      <Col xs={24} md={12}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          Production got slower. Now what?
        </Typography.Title>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {steps.map((step, i) => (
            <Space key={step} align="start" size={12}>
              <Tag style={{ margin: 0, minWidth: 26, textAlign: 'center' }}>{i + 1}</Tag>
              <Typography.Text type="secondary">{step}</Typography.Text>
            </Space>
          ))}
        </Space>
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
          Every step is doable. Together they are an afternoon, and they start after somebody has
          already noticed.
        </Typography.Paragraph>
      </Col>

      <Col xs={24} md={12}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          What PecGoProf does instead
        </Typography.Title>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {[
            'Profiles arrive continuously and are kept, so the comparison already exists.',
            'A deploy is a boundary: the new build is compared with the one it replaced.',
            'A function that took a larger share and cost more is reported, with both numbers.',
            'The alert names the service, the two builds and the function.',
          ].map((line) => (
            <Space key={line} align="start" size={12}>
              <Typography.Text style={{ color: '#52c41a' }}>✓</Typography.Text>
              <Typography.Text type="secondary">{line}</Typography.Text>
            </Space>
          ))}
        </Space>
        <Typography.Paragraph style={{ marginTop: 16, marginBottom: 0 }}>
          <Link to="/demo">Open the demo project →</Link>
        </Typography.Paragraph>
      </Col>
    </Row>
  )
}

/** Where PecGoProf sits next to pprof, without attacking it. */
export function AgainstPprof() {
  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <Card title="Go's own pprof" style={{ height: '100%' }}>
          <Space direction="vertical" size={8}>
            <Typography.Text type="secondary">Captures a profile when you ask.</Typography.Text>
            <Typography.Text type="secondary">Describes one moment of one process.</Typography.Text>
            <Typography.Text type="secondary">
              Comparing two of them is a thing you do by hand.
            </Typography.Text>
            <Typography.Text type="secondary">
              Keeping them, and knowing which build each came from, is your problem.
            </Typography.Text>
          </Space>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card title="PecGoProf" style={{ height: '100%' }}>
          <Space direction="vertical" size={8}>
            <Typography.Text type="secondary">Collects continuously and keeps the history.</Typography.Text>
            <Typography.Text type="secondary">Knows which build each profile came from.</Typography.Text>
            <Typography.Text type="secondary">Compares builds and says what changed.</Typography.Text>
            <Typography.Text type="secondary">Tells you without being asked.</Typography.Text>
          </Space>
        </Card>
      </Col>
      <Col span={24}>
        <Typography.Paragraph style={{ fontSize: 17, marginBottom: 0 }}>
          pprof is the data source and it is excellent. Metrics tell you something got slower;
          pprof tells you what the process was doing; PecGoProf tells you{' '}
          <strong>what changed</strong>.
        </Typography.Paragraph>
      </Col>
    </Row>
  )
}

/**
 * What is measured. Only the types the platform actually collects.
 *
 * Dark, because this is the section that shows the instrument rather than the
 * argument, and the icons — cropped from the same sheet as the gopher — are
 * drawn on a dark ground. Each is a 48px crop shown at 24, so it stays sharp
 * on a retina display without shipping a second set.
 */
export function ProfileTypes() {
  const types: [string, string, string][] = [
    ['CPU', 'cpu', 'Which functions the processor spent its time in.'],
    ['Heap', 'mem', 'What the process is holding, so growth between builds is visible.'],
    ['Allocations', 'alloc', 'How fast it allocates — the usual cause of CPU spent in the runtime.'],
    ['Goroutines', 'gor', 'How many exist and where they were created.'],
    ['Mutex', 'mutex', 'Where contention is costing wall time.'],
    ['Block', 'block', 'Where goroutines wait.'],
  ]

  return (
    <Row gutter={[16, 16]}>
      {types.map(([name, icon, body]) => (
        <Col xs={24} sm={12} md={8} key={name}>
          <Card
            size="small"
            style={{
              height: '100%',
              background: '#12181f',
              borderColor: 'rgba(255,255,255,0.12)',
            }}
          >
            <Space size={10} align="center">
              <img
                src={`/icons/${icon}.webp`}
                alt=""
                aria-hidden
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
                style={{ width: 24, height: 24, borderRadius: 7, display: 'block' }}
              />
              <Typography.Text strong style={{ color: '#fff' }}>
                {name}
              </Typography.Text>
            </Space>
            <Typography.Paragraph
              style={{ color: 'rgba(255,255,255,0.6)', marginTop: 8, marginBottom: 0 }}
            >
              {body}
            </Typography.Paragraph>
          </Card>
        </Col>
      ))}
      <Col span={24}>
        <Typography.Text style={{ color: 'rgba(255,255,255,0.55)' }}>
          Each type is switched on and off independently, with its own interval. Deploy comparison
          uses CPU, heap and allocations — goroutine and mutex counts move with load rather than
          with the build, so a release is not judged on them.
        </Typography.Text>
      </Col>
    </Row>
  )
}
